<script setup>
import { useI18n } from 'vue-i18n';
import NucleoWorkOrderRequestIndicator from './NucleoWorkOrderRequestIndicator.vue';

defineProps({
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['open']);
const { t } = useI18n();
const fallback = value => value || t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED');
const address = row =>
  row.imovel?.logradouro ||
  row.imovel?.title ||
  t('NUCLEO_WORK_ORDERS.TABLE.ADDRESS_UNAVAILABLE');
const addressDetail = row =>
  [row.imovel?.bairro, row.imovel?.cidade, row.imovel?.uf]
    .filter(Boolean)
    .join(' · ');
const postSaleUser = row => row.responsavel_pos_venda || row.solicitante;
const initials = row =>
  fallback(postSaleUser(row)?.name).slice(0, 2).toUpperCase();
const formatDate = value => {
  if (!value) return t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED');
  return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
};
</script>

<template>
  <div class="hidden overflow-x-auto lg:block">
    <table class="min-w-[1240px] w-full border-collapse text-left text-sm">
      <thead
        class="bg-lais-bg-soft text-xs uppercase tracking-wide text-lais-text-muted"
      >
        <tr>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.POST_SALE') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.MANAGER') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.STORE') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.PROPERTY') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.ONUS') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.DISTRICT') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.REQUESTED_AT') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.DUE_AT') }}</th>
          <th class="p-4">{{ t('NUCLEO_WORK_ORDERS.TABLE.SERVICES') }}</th>
          <th class="p-4">
            <span class="sr-only">
              {{ t('NUCLEO_WORK_ORDERS.ACTIONS.VIEW_DETAILS') }}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.id"
          class="cursor-pointer border-t border-lais-border-soft align-top transition hover:bg-lais-primary-50/30"
          :class="row.status_visualizacao_atual === 0 ? 'font-bold' : ''"
          :aria-label="
            row.status_visualizacao_atual === 0
              ? t('NUCLEO_WORK_ORDERS.TABLE.NEW_REQUEST')
              : undefined
          "
          @click="emit('open', row)"
        >
          <td class="p-4">
            <div class="flex items-center gap-2">
              <div
                class="grid size-8 place-items-center rounded-full bg-lais-primary-50 text-xs font-bold text-lais-primary"
              >
                {{ initials(row) }}
              </div>
              <span
                class="text-lais-text"
                :class="
                  row.status_visualizacao_atual === 0
                    ? 'font-bold'
                    : 'font-semibold'
                "
              >
                {{ fallback(postSaleUser(row)?.name) }}
              </span>
              <span v-if="row.status_visualizacao_atual === 0" class="sr-only">
                {{ t('NUCLEO_WORK_ORDERS.TABLE.NEW_REQUEST') }}
              </span>
            </div>
          </td>
          <td class="p-4">
            <div class="flex items-center gap-2">
              <div
                class="grid size-8 place-items-center rounded-full bg-lais-bg-soft text-xs font-bold text-lais-text-muted"
              >
                {{ fallback(row.gerente?.name).slice(0, 2).toUpperCase() }}
              </div>
              <span class="text-lais-text">
                {{ fallback(row.gerente?.name) }}
              </span>
            </div>
          </td>
          <td class="p-4 text-lais-text">{{ fallback(row.loja?.name) }}</td>
          <td class="p-4">
            <p
              class="text-lais-text"
              :class="
                row.status_visualizacao_atual === 0
                  ? 'font-bold'
                  : 'font-semibold'
              "
            >
              {{ address(row) }}
            </p>
            <p v-if="addressDetail(row)" class="text-xs text-lais-text-muted">
              {{ addressDetail(row) }}
            </p>
          </td>
          <td class="p-4">
            <NucleoWorkOrderRequestIndicator :value="row.onus_solicitada" />
          </td>
          <td class="p-4">
            <NucleoWorkOrderRequestIndicator :value="row.vendedor_comarca" />
          </td>
          <td class="p-4 text-lais-text-muted">
            {{ formatDate(row.data_solicitacao) }}
          </td>
          <td class="p-4 text-lais-text-muted">
            {{ formatDate(row.data_previsao) }}
          </td>
          <td class="p-4">
            <div class="flex max-w-sm flex-wrap gap-2">
              <button
                v-for="service in row.servicos"
                :key="service.id"
                type="button"
                class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-xs font-semibold text-lais-primary"
                @click.stop="
                  emit('open', { ...row, selectedServiceId: service.id })
                "
              >
                {{ service.nome }}
              </button>
            </div>
          </td>
          <td class="p-4 text-right">
            <button
              type="button"
              class="rounded-lais bg-lais-primary px-3 py-2 text-xs font-semibold text-white transition hover:bg-lais-primary/90"
              @click.stop="emit('open', row)"
            >
              {{ t('NUCLEO_WORK_ORDERS.ACTIONS.VIEW_DETAILS') }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
